import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { useHasSession } from "core/hooks/use-session-key"
import type {
  OrganizationWithCounts,
  OrganizationGetResponse,
} from "organizations/types"
import { getOrganizationAxios } from "api-hooks/get-organization-axios"
import { buildWorkspaceToOrgMap } from "api-hooks/workspace-org-map"
import {
  useWorkspaceOrganizationId,
  useWorkspaces,
} from "api-hooks/use-workspaces"

export function useOrganizations() {
  const hasSession = useHasSession()

  return useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const axios = getOrganizationAxios()
      const { data } = await axios.post("/seam/console/v1/organizations/list")
      return data.organizations as OrganizationWithCounts[]
    },
    enabled: hasSession,
  })
}

export function useOrganization(organizationId: string | null) {
  const hasSession = useHasSession()

  return useQuery({
    queryKey: ["organizations", organizationId],
    queryFn: async () => {
      const axios = getOrganizationAxios()
      const { data } = await axios.post("/seam/console/v1/organizations/get", {
        organization_id: organizationId,
      })
      return data as OrganizationGetResponse
    },
    enabled: hasSession && !!organizationId,
    retry: false,
  })
}

/**
 * Resolves the organization a given workspace belongs to, along with that org's
 * details (including its sibling workspaces). The org id comes from the
 * workspace's own `organization_id` in `/workspaces/list`. Returns
 * `organization: undefined` while loading or if the workspace isn't assigned to
 * any org.
 */
export function useWorkspaceOrg(workspaceId: string | null | undefined) {
  const { organizationId, isLoading: isResolvingOrg } =
    useWorkspaceOrganizationId(workspaceId)
  const { data: organization, isLoading: isLoadingOrg } =
    useOrganization(organizationId)

  return {
    organizationId,
    organization,
    isLoading: isResolvingOrg || isLoadingOrg,
  }
}

/**
 * Maps every workspace the user can access to its organization, using the
 * `organization_id` each workspace carries in `/workspaces/list`. Unassigned
 * workspaces (null `organization_id`) are in neither collection.
 *
 * `assignedWorkspaceIds` is scoped to the orgs the user is a member of, so a
 * workspace owned by an org the user can't select stays in the classic
 * "Unassigned" bucket and remains reachable from the topbar.
 */
export function useAllAssignedWorkspaceIds() {
  const {
    data: workspaces = [],
    isLoading: isLoadingWorkspaces,
    isError: isWorkspacesError,
  } = useWorkspaces()
  const {
    data: organizations = [],
    isLoading: isLoadingOrgs,
    isError: isOrgsError,
  } = useOrganizations()

  const { assignedWorkspaceIds, workspaceToOrgMap } = useMemo(
    () =>
      buildWorkspaceToOrgMap(
        workspaces,
        organizations.map((org) => org.organization_id)
      ),
    [workspaces, organizations]
  )

  return {
    assignedWorkspaceIds,
    workspaceToOrgMap,
    isLoading: isLoadingWorkspaces || isLoadingOrgs,
    hasErrors: isWorkspacesError || isOrgsError,
  }
}
